import { FormEvent, useContext, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography, useMediaQuery } from '@mui/material'
import { Stack, useTheme } from '@mui/system'
import InputField from '../../../components/InputFields/InputField'
import SelectField from '../../../components/InputFields/SelectField'
import { Contact, ContactRole, EventDto } from '../../../interfaces/IEvent'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import { enqueueSnackbar } from 'notistack'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'

interface EditContactProps {
  viewEvent: EventDto
  selectedContact: Contact
  onUpdateEventView: (eventId: string) => void
}

export default function UpdateContact({ viewEvent, selectedContact, onUpdateEventView }: EditContactProps) {
  const { context } = useContext(EbAuthenticatedAppContext)
  const [openEditContact, setOpenUpdateContact] = useState<boolean>(false)
  const [contact, setContact] = useState<Contact>(selectedContact)
  const theme = useTheme()
  const smFullscreen = useMediaQuery(theme.breakpoints.down('sm'))

  const handleOpenUpdateContact = () => {
    setOpenUpdateContact(true)
  }

  const handleCloseUpdateContact = () => {
    setOpenUpdateContact(false)
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const response = await context.restApiClient.buildRequest(`/api/event/${viewEvent.id}/contacts/update`).serializeToJsonContent(contact).putAsync()

    if (response.error) {
      enqueueSnackbar(<Typography variant="body2">Kunne ikke endre kontakt. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
        variant: 'error',
      })
    } else {
      enqueueSnackbar(<Typography variant="body2">Kontakt endret.</Typography>, { variant: 'success' })
      handleCloseUpdateContact()
      onUpdateEventView(viewEvent.id)
    }
  }

  return (
    <>
      <IconButton color="warning" size="small" onClick={handleOpenUpdateContact}>
        <EditOutlinedIcon fontSize="small" />
      </IconButton>
      <Dialog
        open={openEditContact}
        onClose={handleCloseUpdateContact}
        fullWidth
        maxWidth="xs"
        fullScreen={smFullscreen}
        scroll={'paper'}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: 'background.default',
          },
        }}
      >
        <DialogTitle>Endre kontakt</DialogTitle>
        <form
          onSubmit={handleSubmit}
          style={{
            display: 'contents',
          }}
        >
          <DialogContent dividers>
            <Stack direction={'column'} spacing={1}>
              <SelectField
                name="role"
                title="Rolle*"
                required={true}
                value={contact.role}
                selectOptions={Object.values(ContactRole)}
                onChange={(e) => setContact({ ...contact, role: e.target.value })}
              />
              <InputField
                name="name"
                title="Navn*"
                required={true}
                size="medium"
                placeholder="Navn"
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                maxLength={30}
              />
              <InputField
                name="phone"
                title={ContactRole.Kontaktperson === contact.role ? 'Telefon*' : 'Telefon'}
                required={ContactRole.Kontaktperson === contact.role ? true : false}
                size="medium"
                placeholder="Telefon"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                maxLength={11}
              />
              <InputField
                name="email"
                title="Epost"
                size="medium"
                placeholder="Epost"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                maxLength={50}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack
              direction={'row'}
              spacing={1}
              sx={{
                width: '100%',
                justifyContent: 'right'
              }}>
              <Button variant="contained" color="error" onClick={handleCloseUpdateContact}>
                Avbryt
              </Button>
              <Button variant="contained" color="primary" type="submit">
                Legg til
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
